import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, Repeat } from 'lucide-react';

const keyframeCSS = `
  @media (max-width: 600px) {
    .offer-card {
      padding: 16px 20px !important;
      max-width: 100% !important;
    }
    .offer-amount { font-size: clamp(1.8rem, 8vw, 2.5rem) !important; }
    .offer-label { font-size: 0.72rem !important; }
  }
  @keyframes offerPhoneRing {
    0%, 100% { transform: rotate(0deg) scale(1); }
    5% { transform: rotate(-20deg) scale(1.1); }
    10% { transform: rotate(20deg) scale(1.1); }
    15% { transform: rotate(-20deg) scale(1.1); }
    20% { transform: rotate(20deg) scale(1.1); }
    25% { transform: rotate(-15deg) scale(1.05); }
    30% { transform: rotate(15deg) scale(1.05); }
    35% { transform: rotate(0deg) scale(1); }
  }
  @keyframes offerBorderGlow {
    0%, 100% { border-color: rgba(212,175,55,0.25); box-shadow: 0 0 25px rgba(212,175,55,0.08), inset 0 0 25px rgba(212,175,55,0.04); }
    50% { border-color: rgba(212,175,55,0.6); box-shadow: 0 0 50px rgba(212,175,55,0.2), 0 0 80px rgba(212,175,55,0.08), inset 0 0 35px rgba(212,175,55,0.06); }
  }
  @keyframes offerNeonText {
    0%, 19%, 21%, 23%, 25%, 54%, 56%, 100% {
      text-shadow: 0 0 10px rgba(212,175,55,0.7), 0 0 25px rgba(212,175,55,0.3), 0 0 50px rgba(212,175,55,0.15);
      opacity: 1;
    }
    20%, 24%, 55% {
      text-shadow: 0 0 5px rgba(212,175,55,0.2);
      opacity: 0.85;
    }
  }
  @keyframes slotSpin {
    0% { transform: translateY(-25px); opacity: 0; filter: blur(3px); }
    60% { transform: translateY(3px); opacity: 1; filter: blur(0); }
    100% { transform: translateY(0); opacity: 1; filter: blur(0); }
  }
  @keyframes offerShine {
    0% { left: -100%; }
    50%, 100% { left: 200%; }
  }
  @keyframes phoneGlow {
    0%, 100% { filter: drop-shadow(0 0 8px rgba(212,175,55,0.4)); }
    50% { filter: drop-shadow(0 0 20px rgba(212,175,55,0.8)) drop-shadow(0 0 40px rgba(212,175,55,0.3)); }
  }
  @keyframes offerCornerLight {
    0%, 100% { opacity: 0.3; }
    50% { opacity: 0.8; }
  }
  @keyframes offerAmountShimmer {
    0% { background-position: -200% center; }
    100% { background-position: 200% center; }
  }
`;

function formatEuro(amount) {
  if (typeof amount !== 'number') return String(amount);
  return amount.toLocaleString('it-IT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: amount < 1 ? 2 : 0,
    maximumFractionDigits: 2,
  });
}

const statusColors = {
  pending: { backgroundColor: 'rgba(243, 156, 18, 0.12)', color: '#f39c12', borderColor: 'rgba(243,156,18,0.3)' },
  accepted: { backgroundColor: 'rgba(46, 204, 113, 0.12)', color: '#2ecc71', borderColor: 'rgba(46,204,113,0.3)' },
  rejected: { backgroundColor: 'rgba(231, 76, 60, 0.12)', color: '#e74c3c', borderColor: 'rgba(231,76,60,0.3)' },
};

const statusLabels = {
  pending: 'In attesa',
  accepted: 'Accettata',
  rejected: 'Rifiutata',
};

export default function OfferDisplay({ lastOffer }) {
  const [displayAmount, setDisplayAmount] = useState(null);

  useEffect(() => {
    if (!lastOffer || lastOffer.amount === undefined || lastOffer.amount === null) {
      setDisplayAmount(null);
      return;
    }

    const target = lastOffer.amount;
    const steps = 15;
    const stepDuration = 45;
    let step = 0;

    const interval = setInterval(() => {
      step++;
      if (step >= steps) {
        setDisplayAmount(target);
        clearInterval(interval);
      } else {
        const randomFactor = 1 - (step / steps);
        const randomOffset = (Math.random() - 0.5) * target * randomFactor * 2;
        setDisplayAmount(Math.max(0, Math.round(target + randomOffset)));
      }
    }, stepDuration);

    return () => clearInterval(interval);
  }, [lastOffer]);

  if (!lastOffer) return null;

  const { amount, status, swap_box_number, swap_region_name } = lastOffer;
  const isSwap = !!swap_box_number;

  return (
    <AnimatePresence mode="wait">
      <motion.div
        className="offer-card"
        key={`${amount}-${status}`}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '18px',
          padding: '32px 36px',
          background: `
            linear-gradient(135deg, rgba(10,10,30,0.97), rgba(15,15,40,0.95)),
            radial-gradient(ellipse at 50% 0%, rgba(212,175,55,0.06) 0%, transparent 60%)
          `,
          borderRadius: '20px',
          border: '2px solid rgba(212,175,55,0.3)',
          width: '100%',
          maxWidth: '500px',
          position: 'relative',
          overflow: 'hidden',
          animation: 'offerBorderGlow 3s ease-in-out infinite',
        }}
        initial={{ opacity: 0, scale: 0.8, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.85, y: -15 }}
        transition={{ type: 'spring', stiffness: 180, damping: 18 }}
      >
        <style>{keyframeCSS}</style>

        {/* Shine overlay */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: '-100%',
          width: '50%',
          height: '100%',
          background: 'linear-gradient(90deg, transparent, rgba(212,175,55,0.04), transparent)',
          animation: 'offerShine 5s ease-in-out infinite',
          pointerEvents: 'none',
        }} />

        {/* Corner accent lights */}
        {[
          { top: '8px', left: '8px' },
          { top: '8px', right: '8px' },
          { bottom: '8px', left: '8px' },
          { bottom: '8px', right: '8px' },
        ].map((pos, i) => (
          <div key={i} style={{
            position: 'absolute',
            ...pos,
            width: '3px',
            height: '3px',
            borderRadius: '50%',
            backgroundColor: 'rgba(212,175,55,0.5)',
            boxShadow: '0 0 6px rgba(212,175,55,0.4)',
            animation: `offerCornerLight 2s ease-in-out ${i * 0.3}s infinite`,
          }} />
        ))}

        {/* Inner border glow line at top */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: '10%',
          right: '10%',
          height: '1px',
          background: 'linear-gradient(90deg, transparent, rgba(212,175,55,0.4), transparent)',
        }} />

        {/* Phone icon + label */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}>
          <div style={{
            color: '#D4AF37',
            animation: 'offerPhoneRing 2.5s ease-in-out infinite, phoneGlow 2s ease-in-out infinite',
          }}>
            {isSwap ? <Repeat size={24} /> : <Phone size={24} />}
          </div>
          <span className="offer-label" style={{
            fontFamily: 'var(--font-display)',
            fontSize: '0.9rem',
            fontWeight: 600,
            color: '#D4AF37',
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            animation: 'offerNeonText 3s ease-in-out infinite',
          }}>
            {isSwap ? 'IL BANCHIERE PROPONE UNO SCAMBIO' : 'IL BANCHIERE PROPONE'}
          </span>
        </div>

        {/* Amount or swap info */}
        {isSwap ? (
          <motion.div
            className="offer-amount"
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'clamp(1.8rem, 4vw, 2.5rem)',
              fontWeight: 700,
              background: 'linear-gradient(90deg, #B8960C, #D4AF37, #F0D060, #D4AF37, #B8960C)',
              backgroundSize: '300% auto',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              animation: 'offerAmountShimmer 3s linear infinite',
              filter: 'drop-shadow(0 0 15px rgba(212,175,55,0.4))',
            }}
            initial={{ scale: 0, rotate: -5 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: 'spring', stiffness: 180, damping: 14, delay: 0.2 }}
          >
            Pacco {swap_box_number}
            {swap_region_name && (
              <span style={{
                display: 'block',
                fontSize: '1rem',
                fontWeight: 400,
                WebkitTextFillColor: 'rgba(212,175,55,0.7)',
                marginTop: '4px',
              }}>
                {swap_region_name}
              </span>
            )}
          </motion.div>
        ) : (
          <motion.div
            className="offer-amount"
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'clamp(2.5rem, 7vw, 4rem)',
              fontWeight: 700,
              background: 'linear-gradient(90deg, #8B6914, #D4AF37, #F0D060, #FFE88A, #F0D060, #D4AF37, #8B6914)',
              backgroundSize: '300% auto',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              letterSpacing: '0.02em',
              animation: displayAmount === amount ? 'offerAmountShimmer 3s linear infinite' : 'slotSpin 0.08s ease-out',
              filter: 'drop-shadow(0 0 25px rgba(212,175,55,0.5))',
            }}
            initial={{ scale: 0, rotate: -5 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: 'spring', stiffness: 180, damping: 14, delay: 0.2 }}
          >
            {formatEuro(displayAmount !== null ? displayAmount : amount)}
          </motion.div>
        )}

        {/* Status badge */}
        {status && (
          <motion.div
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: '0.8rem',
              fontWeight: 600,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              padding: '5px 20px',
              borderRadius: '9999px',
              border: '1px solid',
              ...(statusColors[status] || statusColors.pending),
            }}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            {statusLabels[status] || status}
          </motion.div>
        )}
      </motion.div>
    </AnimatePresence>
  );
}
